import React from 'react'

import numeral from 'numeral'

import { Button } from '@cgtrader/triangle-ui/dist/src/ssr-index'
import { array, bool, func, object, string } from 'prop-types'

import i18n from 'Pods/Shared/I18n'
import SvgTriangleUIIcon from 'Pods/Shared/SvgTriangleUIIcon'
import CartItemsList from 'Pods/UnifiedNavigation/Shared/CartItemsList/CartItemsList'

import DiscountInfo from './Components/DiscountInfo/DiscountInfo'
import SaleProgress from './Components/SaleProgress/SaleProgress'

function FullCart({
  items,
  meta,
  included,
  onLoadNextPage,
  isLastPageLoaded,
  locale,
  trackCartRemoveGA4,
  isCartBelowThreshold,
  isCartAboveThreshold,
  shouldShowCartProgress,
  onCloseCart = null,
}) {
  function prepareTrackViewItemList(attributes) {
    let trackableAttributes = {}
    const searchQuery = attributes.searchQuery

    trackableAttributes = {
      item_id: String(attributes.id),
      item_name: attributes.title,
      affiliation: attributes.affiliation,
      currency: 'USD',
      index: attributes.index,
      item_brand: attributes.authorUsername,
      item_category: attributes.category,
      item_category2: attributes.subcategory,
      item_category3: attributes.itemCategory3,
      item_category4: attributes.itemCategory4,
      item_category5: attributes.itemCategory5,
      item_list_id: searchQuery,
      item_list_name: searchQuery,
      item_variant: attributes.productType,
      location_id: attributes.locationId && attributes.locationId.join('_'),
      price: parseFloat(attributes.price),
      quantity: 1,
    }

    if (attributes.discount > 0) {
      trackableAttributes.discount = parseFloat(attributes.discount)
    }

    return trackableAttributes
  }

  function trackBeginCheckoutGA4() {
    if (window.dataLayer) {
      const list = meta.trackingCartItems.map((item) => prepareTrackViewItemList(item))

      window.dataLayer.push({
        event: 'begin_checkout',
        ecommerce: {
          currency: 'USD',
          value: meta.totalPrice,
          items: list,
        },
      })
    }
  }

  function handleMouseDown(event) {
    if (event.button === 1) {
      trackBeginCheckoutGA4()
    }
  }

  function renderCldInfoRow() {
    if (!meta.displayCldInfo || meta.saleCampaignActive) {
      return null
    }

    if (meta.remainerForCld > 0) {
      return (
        <div className="popover__pricing">
          <div className="wallet-icon-tag">
            <SvgTriangleUIIcon type="wallet" color="light" size="tiny" />
          </div>
          {i18n.t('navigation.cart_widget.remainer', { ns: 'rails', lng: locale })}
          <span className="text-semibold">{numeral(meta.remainerForCld).format('$0.00')}</span>
        </div>
      )
    }

    return (
      <div className="popover__pricing">
        <div className="wallet-icon-tag">
          <SvgTriangleUIIcon type="wallet" color="light" size="tiny" />
        </div>
        {i18n.t('navigation.cart_widget.used', { ns: 'rails', lng: locale })}
        <span className="text-semibold">{numeral(meta.totalCreditsUsed).format('$0.00')}</span>
      </div>
    )
  }

  const potentialDiscount = items.reduce((acc, val) => {
    const {
      attributes: { price, originalPrice },
    } = val
    const difference = originalPrice - price

    if (difference !== 0) {
      return acc + difference
    }

    return acc
  }, 0)

  return (
    <div id="cart-box-content" className="popover-content no-pad-left-right popover__cart">
      <div className="nav-options-dropdown__section">
        <CartItemsList
          items={items}
          included={included}
          onLoadNextPage={onLoadNextPage}
          isLastPageLoaded={isLastPageLoaded}
          trackCartRemoveGA4={trackCartRemoveGA4}
          isCartBelowThreshold={isCartBelowThreshold}
          isCartAboveThreshold={isCartAboveThreshold}
          shouldShowCartProgress={shouldShowCartProgress}
          saleThreshold={meta.saleThreshold}
          locale={locale}
        />
        <div className="popover-content__footer">
          <div className="popover__pricing-container">
            {shouldShowCartProgress && (
              <SaleProgress saleThreshold={meta.saleThreshold} totalPrice={meta.totalPrice} locale={locale} />
            )}
            {(meta.discountWithoutCld > 0 || isCartBelowThreshold) && (
              <div className="popover__pricing">
                <DiscountInfo
                  isCartBelowThreshold={isCartBelowThreshold}
                  potentialDiscount={potentialDiscount}
                  discountWithoutCld={meta.discountWithoutCld}
                  locale={locale}
                />
              </div>
            )}
            {renderCldInfoRow()}
            {meta.couponCreditsUsed > 0 && (
              <div className="popover__pricing">
                {i18n.t('navigation.cart_widget.discount', { ns: 'rails', lng: locale })}
                <span className="text-semibold">{numeral(meta.couponCreditsUsed).format('$0.00')}</span>
              </div>
            )}

            <div className="popover__pricing popover__pricing-total text-semibold">
              {i18n.t('navigation.cart_widget.final', { ns: 'rails', lng: locale })}
              <span>
                {meta.totalPrice > meta.totalPay && (
                  <span className="text-semibold disabled cgt-mr-4">{numeral(meta.totalPrice).format('$0.00')}</span>
                )}
                <span className="text-semibold">{numeral(meta.totalPay).format('$0.00')}</span>
              </span>
            </div>
          </div>

          <div className="popover__actions">
            <a
              className="cgt-button cgt-button--primary cgt-button--medium cgt-button--width-expand"
              href="/cg/checkout/payment"
              onClick={trackBeginCheckoutGA4}
              onMouseDown={handleMouseDown}
            >
              <span>{i18n.t('navigation.cart_widget.proceed', { ns: 'rails', lng: locale })}</span>
            </a>
            <div className="popover__actions-continue-shopping-mobile">
              <Button
                type="secondary"
                expand
                onClick={onCloseCart}
                aria-label="Continue shopping"
              >
                {i18n.t('navigation.cart_widget.continue_shopping', { ns: 'rails', lng: locale })}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

FullCart.propTypes = {
  items: array.isRequired,
  meta: object.isRequired,
  included: array.isRequired,
  onLoadNextPage: func.isRequired,
  isLastPageLoaded: bool.isRequired,
  locale: string.isRequired,
  trackCartRemoveGA4: func.isRequired,
  isCartBelowThreshold: bool.isRequired,
  isCartAboveThreshold: bool.isRequired,
  shouldShowCartProgress: bool.isRequired,
  onCloseCart: func,
}

export default FullCart
